/**
 * `PickerState` and the pure functions over it — FRAME_PICKER_DESIGN.md §6.4, §3.2, §6.5.
 *
 * The root component HOLDS this object (in a `ReplaySubject(1)`, §6.4) and calls `reduce` for
 * every user action; nothing here touches React, the DOM, the options context or storage. The
 * §3.2 semantics are mirrored so the estimate and the check agree with what ironman will do —
 * "ironman is authoritative; the browser mirrors it for the estimate".
 *
 * Mode vocabulary: the picker's own `ONE | SEQUENCE | SHEET` (the processing-note words, P2.6)
 * maps 1:1 onto the xconfig values in `MODE_TO_OPTION`; the two are never mixed.
 *
 * Two counts since round 6 (R6-I5): `requestedFrames` is the number ironman validates and asks
 * ffmpeg for (`req.frames`); `frameCount` is the number ffmpeg WRITES under ironman's select rule
 * (`media/estimate.ts#deliveredFrames`, R14 BUG-002: the first source frame at or after each `start + n · I − 1 ms`,
 * while one exists). Refusals and the big-job check read the first; everything the user sees as "N frames" reads
 * the second, and the grid / sheet preview (`frameTimes`) names the very frames the ZIP will hold.
 */
import { deliveredFrames, countSampleEnd, selectedFrameIndex } from '../media/estimate';
import { createTimeline, floorMs, frameIndex, frameStart } from '../media/timeline';

export type PickerMode = 'ONE' | 'SEQUENCE' | 'SHEET';
export type Spacing = 'BY_INTERVAL' | 'BY_COUNT';
export type PickerStep = 'edit' | 'check' | 'progress';
export type PickerSource = 'original' | 'preview' | 'none';

export interface TimeRange {
    start: number;
    end: number;
}

export interface SheetSettings {
    columns: number;
    /** Pixels; the ONLY size in `SHEET` (the page's `resolution` is ignored, §3.2). */
    width: number;
    timestamps: boolean;
    header: boolean;
}

export interface PickerState {
    sid: string;
    /** `UploadInProgressFile.uuid` of the selected staged file, or null before one is staged. */
    fileId: string | null;
    /** The playhead, in seconds. `screenshotTime` on Convert in `ONE`. */
    t: number;
    mode: PickerMode;
    /** null = whole clip (both option values empty, §3.1). */
    range: TimeRange | null;
    spacing: Spacing;
    /** Seconds per frame under `BY_INTERVAL`; always one of `INTERVAL_STEPS`. */
    interval: number;
    /** Frames under `BY_COUNT`; ≥ 2 (§3.2). */
    count: number;
    sheet: SheetSettings;
    step: PickerStep;
    zoom: 1 | 2;
    sharpnessReady: boolean;
    source: PickerSource;
    /**
     * Round 12 (N3, the owner's P4): the frames the user PINNED (their times, seconds, in time order, ≤
     * `MAX_PINS`). Persisted with the record so Back from /downloads keeps them; the thumbnails are the
     * root's session-only bytes.
     */
    pins: number[];
    /**
     * Round 12 (N3, P4): WHAT YOU GET = "Picked frames" -- Convert makes one single-frame job per pin. A
     * fourth choice beside the tree's three, so it is not a tree mode: while true the tree holds ONE
     * (`screenshotTime` is written per job). Selected by every pin; false again when the pins go or the
     * user picks One frame / Several frames / Contact sheet (the pins stay). Never true with no pins.
     */
    picked: boolean;
}

/**
 * IT1 (W3, saas #8 / qa #8 -- "one number"): how long a picker record (and the files it names) is kept: 2.5 hours,
 * the Studio's `STUDIO_TTL_MS` (`components/studio/state/persistence.ts`, which this module may not import) and the
 * ceiling the edge sweeps at. §6.4's 8 h promised a restore the server could not keep ("This video isn't on our
 * servers any more" around a restored playhead after 3 h). The copy reads the hours from here.
 */
export const FRAMEPICKER_TTL_MS = 2.5 * 60 * 60 * 1000;
/** `FRAMEPICKER_TTL_MS` as the copy says it: "2.5 hours". */
export const FRAMEPICKER_TTL_HOURS = FRAMEPICKER_TTL_MS / (60 * 60 * 1000);

/** Round 12 (N3, P4): how many frames can be pinned -- one single-frame job each on Convert. */
export const MAX_PINS = 20;
/** Two pin times within this (half a millisecond) are the same pin. R14: pins are exact frame times now; the epsilon stays. */
export const PIN_EPSILON = 5e-4;

/**
 * §4.3, mirrored in the browser as constants (D-3): amber past 300, refusal text past 600; a
 * sheet refuses past 100 cells or 4 096 px.
 */
export const FRAME_LIMITS = Object.freeze({
    amberAt: 300,
    maxFramesPerJob: 600,
    maxSheetCells: 100,
    maxSheetWidthPx: 4096,
    minSheetWidthPx: 640,
    maxSheetColumns: 12,
    minCount: 2,
});

/** §6.5: the check step shows first when `frames ≥ 50 || (duration > 120 s && height > 1080)`. */
export const CHECK_THRESHOLDS = Object.freeze({ frames: 50, durationS: 120, heightPx: 1080 });

/** §6.5: the amber "Large job" line when the estimate exceeds 50 MB. */
export const LARGE_JOB_BYTES = 50 * 1024 * 1024;

/**
 * The closed set `screenshotFramerate` accepts — xconfig `video-duration.json` values, seconds
 * per frame, fastest first. The option is the STRING; the number is what the estimate uses.
 */
export const INTERVAL_STEPS: readonly { readonly option: string; readonly seconds: number }[] = Object.freeze([
    { option: '1/60', seconds: 1 / 60 },
    { option: '1/50', seconds: 1 / 50 }, // SRV3c (xconfig 0d0636e): 50 fps aligns end to end
    { option: '1/30', seconds: 1 / 30 },
    { option: '1/25', seconds: 1 / 25 }, // SRV3c (xconfig 0d0636e): 25 fps aligns end to end
    { option: '1/24', seconds: 1 / 24 },
    { option: '1/10', seconds: 1 / 10 },
    { option: '1/5', seconds: 1 / 5 },
    { option: '1/3', seconds: 1 / 3 },
    { option: '1/2', seconds: 1 / 2 },
    { option: '1', seconds: 1 },
    { option: '2', seconds: 2 },
    { option: '3', seconds: 3 },
    { option: '4', seconds: 4 },
    { option: '5', seconds: 5 },
    { option: '6', seconds: 6 },
    { option: '7', seconds: 7 },
    { option: '8', seconds: 8 },
    { option: '9', seconds: 9 },
    { option: '10', seconds: 10 },
]);

/**
 * Editor re-open T5 (R-D): the xconfig's FOURTH `videoToImageFrameSelection` value -- the pinned frames as one job (one workflow,
 * N files). Not a picker tree mode (`PickerMode`): Picked frames is entered by pinning (`PickerState.picked`), never as a chip.
 */
export const PICKED_FRAMES_OPTION = 'SCREENSHOT_PICKED_FRAMES';

/** The picker's mode word ↔ the xconfig `videoToImageFrameSelection` value (§3.1). */
export const MODE_TO_OPTION: Readonly<Record<PickerMode, string>> = Object.freeze({
    ONE: 'SCREENSHOT_SPECIFIC_FRAME',
    SEQUENCE: 'SCREENSHOT_MULTIPLE_FRAMES',
    SHEET: 'CONTACT_SHEET',
});

export function modeFromOption(value: unknown): PickerMode | null {
    switch (value) {
        case 'SCREENSHOT_SPECIFIC_FRAME': return 'ONE';
        case 'SCREENSHOT_MULTIPLE_FRAMES': return 'SEQUENCE';
        case 'CONTACT_SHEET': return 'SHEET';
        default: return null;
    }
}

/** The xconfig defaults (§3.1): 12 frames, 4 columns, 1920 px, timestamps + header on. */
export const DEFAULT_SHEET: Readonly<SheetSettings> = Object.freeze({
    columns: 4, width: 1920, timestamps: true, header: true,
});

export function initialPickerState(sid: string, fileId: string | null = null): PickerState {
    return {
        sid,
        fileId,
        t: 0,
        mode: 'ONE',
        range: null,
        spacing: 'BY_INTERVAL',
        interval: 1,
        count: 12,
        sheet: { ...DEFAULT_SHEET },
        step: 'edit',
        zoom: 1,
        sharpnessReady: false,
        source: 'none',
        pins: [],
        picked: false,
    };
}

// ---------------------------------------------------------------------------------------------
// Option value encoding (§3.1/§3.2): `"<seconds>::SECONDS"`, `"<px>::PIXEL"`, ≤ 3 decimals.
// ---------------------------------------------------------------------------------------------

/**
 * Seconds rounded to the millisecond -- the WIRE's precision (`encodeSeconds`), and nothing else's. R14 (BUG-001): the
 * playhead `t`, the pins and the frame times are carried EXACT; rounding them here put `1.4333…` (frame 43 at 30 fps)
 * at `1.433`, inside frame 42, for every k ≡ 1 (mod 3).
 */
export function roundMs(t: number): number {
    return Math.round(t * 1000) / 1000;
}

export function encodeSeconds(t: number): string {
    return `${roundMs(Math.max(0, t))}::SECONDS`;
}

export function encodePixels(px: number): string {
    return `${Math.round(px)}::PIXEL`;
}

/**
 * `"1.5::SECONDS"` → 1.5; `"2"` → 2; `""`, `null`, garbage → null. ironman keeps
 * `split("::").first()`, and so does this.
 */
export function decodeUnitValue(value: unknown): number | null {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value !== 'string') return null;
    const head = value.split('::')[0].trim();
    if (head === '') return null;
    const n = Number(head);
    return Number.isFinite(n) ? n : null;
}

/** `"1/30"` → 0.0333…; `"2"` → 2; anything else → null. */
export function decodeInterval(value: unknown): number | null {
    if (typeof value !== 'string') return null;
    const step = INTERVAL_STEPS.find((s) => s.option === value.trim());
    return step ? step.seconds : null;
}

/** The nearest allowed `screenshotFramerate` option for a seconds-per-frame value. */
export function framerateOptionFor(intervalS: number): string {
    let best = INTERVAL_STEPS[0];
    for (const step of INTERVAL_STEPS) {
        if (Math.abs(step.seconds - intervalS) < Math.abs(best.seconds - intervalS)) best = step;
    }
    return best.option;
}

/** Snap a free interval onto the closed set, so state and option can never disagree. */
export function snapInterval(intervalS: number): number {
    return decodeInterval(framerateOptionFor(intervalS)) as number;
}

// ---------------------------------------------------------------------------------------------
// §3.2 semantics, mirrored.
// ---------------------------------------------------------------------------------------------

/** `[start, end]` clamped to `[0, duration]`; null range = the whole clip. */
export function effectiveRange(state: Pick<PickerState, 'range'>, duration: number): TimeRange {
    const d = Math.max(0, duration);
    if (!state.range) return { start: 0, end: d };
    const start = Math.min(Math.max(0, state.range.start), d);
    const end = Math.min(Math.max(0, state.range.end), d);
    return { start, end };
}

/** §3.2: `start ≥ end` after clamping is `RANGE_INVALID`. */
export function rangeIsValid(state: Pick<PickerState, 'range'>, duration: number): boolean {
    const r = effectiveRange(state, duration);
    return r.start < r.end;
}

/**
 * R14 (BUG-023/024, spec 5): the range START as the wire carries it -- a QUARTER frame before the start of the frame
 * containing `start`, floored to the millisecond, never below 0. ironman (`1ac91e8d`) runs `-ss <start> -to <end + i/2>
 * -i … -vf select='gte(t+0.001\,selected_n*<i>)' -fps_mode passthrough` (`FrameExtraction.kt#buildSequenceCommand`):
 * `-ss` as an input option keeps the frames with pts ≥ start, so a millisecond-ROUNDED start (1.46667 → "1.467") is
 * past frame 44's pts and the sequence began on 45; a quarter frame early puts every `start + n · i` instant a quarter
 * frame (plus the rule's 1 ms) before the frame it names, clear of both neighbours. Without a frame rate (no
 * timeline yet, or a provisional one) the start is written as it is.
 */
export function rangeStartWrite(start: number, duration: number, fps?: number | null): number {
    const s = Math.max(0, Number.isFinite(start) ? start : 0);
    if (!(typeof fps === 'number' && fps > 0) || !(duration > 0)) return s;
    const tl = createTimeline(duration, fps);
    return Math.max(0, floorMs((frameIndex(s, tl) - 0.25) / fps));
}

/**
 * R14 (spec 5): the range as WRITTEN, decoded -- what ironman will compute with. `start` is `rangeStartWrite`; `end` is
 * the sampled end rounded to the millisecond (`encodeSeconds`), or the clip's end when the key is empty. Every count
 * (`requestedFrames`, `frameCount`, `derivedInterval`) reads these two numbers, so the client's "N frames" equals the
 * server's `floor((end − start) / i) + 1` by construction (IT1 be #7 rule) rather than by luck on a float boundary.
 */
export function writtenRange(state: PickerState, duration: number, fps?: number | null): TimeRange {
    const r = effectiveRange(state, duration);
    const start = state.range ? rangeStartWrite(r.start, duration, fps) : 0;
    const end = sampledEnd(state, duration, fps);
    return { start, end: end < r.end || state.range ? roundMs(end) : r.end };
}

/**
 * How many frames ironman REQUESTS (§3.2, `FramePickerRequest.kt:218/:223`): `BY_COUNT` → `count`
 * exactly; `BY_INTERVAL` → one at `start` and one every `interval` while ≤ `end`. `ONE` is always 1.
 * An invalid range is 0. The §4.3 refusals and the §6.5 check read THIS number, as the edge does.
 * R14: over the WRITTEN start and end (`writtenRange`) -- the numbers the server sees.
 */
export function requestedFrames(state: PickerState, duration: number, fps?: number | null): number {
    if (state.mode === 'ONE') return 1;
    const r = effectiveRange(state, duration);
    if (r.start >= r.end) return 0;
    if (state.spacing === 'BY_COUNT') return Math.max(FRAME_LIMITS.minCount, Math.floor(state.count));
    if (!(state.interval > 0)) return 0;
    const w = writtenRange(state, duration, fps);
    if (w.start >= w.end) return 0;
    // IT1 (W3, be #7): TEXTUALLY the server's expression (`FramePickerRequest.parseRange`: `floor((end - start) / i) + 1`),
    // no epsilon -- the client used to show N where the ZIP held N − 1 on a range that is a float-imperfect multiple
    // (0.3 / 0.1 = 2.9999…). ironman is authoritative (D-3); the two sides now disagree about nothing.
    return Math.floor((w.end - w.start) / state.interval) + 1;
}

/**
 * How many frames the edge WRITES: the requested number, less those whose instant `start + n · I − 1 ms` is past the
 * last frame (`media/estimate.ts#deliveredFrames`, R14 BUG-002: ironman `1ac91e8d`'s select rule, over the WRITTEN
 * start and end). A 10 s clip at 1 s is 10 (the 10 s instant has no frame; the last one is at 9.967 at 30 fps --
 * the clip's rate, or the 30 ironman and the provisional timeline assume) and "Exactly 12" over it is 12 (with the
 * rate the last sample is the last frame, `sampledEnd`). The header, the progress screen, the summary chip, the grid
 * and the sheet all read this one, so they agree with the ZIP.
 */
export function frameCount(state: PickerState, duration: number, fps?: number | null, variable = false): number {
    if (state.mode === 'ONE') return 1;
    const requested = requestedFrames(state, duration, fps);
    if (requested === 0) return 0;
    const w = writtenRange(state, duration, fps);
    // Sr review 2 NIT 35: a VARIABLE-rate clip has no grid to anchor the last frame on (the write is passthrough, the start as typed), so the
    // count is the written numbers alone -- every requested instant `start + n · I` inside the clip (the end instant is never a frame).
    if (variable) return writtenInstantsInside(requested, derivedInterval(state, duration, fps), w.start, duration);
    return deliveredFrames(requested, derivedInterval(state, duration, fps), w.start, w.end, duration, fps);
}

/**
 * NIT 35: how many of `requested` instants `start + n · interval` fall strictly inside the clip (`duration` unknown → all).
 * Sr review 6 SHOULD 7 (boundary): strictly-inside is right AT the duration; the true cutoff is the last frame's own pts, which the client
 * does not know on a variable-rate clip -- an instant between the last frame's pts and the end counts here and is not written, so the count
 * can be high by at most one at the very end of the clip.
 */
export function writtenInstantsInside(requested: number, interval: number, start: number, duration: number): number {
    if (!(requested > 0)) return 0;
    if (!(duration > 0) || !Number.isFinite(duration)) return requested;
    if (!(interval > 0)) return start < duration ? Math.min(requested, 1) : 0;
    let n = 0;
    while (n < requested && start + n * interval < duration) n++;
    return n;
}

/**
 * The computed timestamps the grid and the sheet preview show (§6.2): `frameCount` of them. With the clip's `fps`
 * (R14, BUG-002) each is the START of the frame ironman's select rule writes as output `n` (`selectedFrameIndex`
 * over the WRITTEN start and `derivedInterval`) -- the frames the ZIP / sheet WILL hold, by construction: the
 * range's own first frame `k/fps`, never the quarter-frame-early wire instant, and never rounded to the millisecond
 * (the grabber asked for `1.433` and drew frame 42 for a range starting on 43). Without a rate: `start + i · step`
 * from the range's start, the last of a `BY_COUNT` run pinned on the sampled end. `ONE` is the exact playhead.
 */
export function frameTimes(state: PickerState, duration: number, fps?: number | null, variable = false): number[] {
    if (state.mode === 'ONE') return [state.t];
    const n = frameCount(state, duration, fps, variable);
    if (n === 0) return [];
    const step = derivedInterval(state, duration, fps);
    if (typeof fps === 'number' && fps > 0 && duration > 0) {
        const tl = createTimeline(duration, fps);
        const w = writtenRange(state, duration, fps);
        return Array.from({ length: n }, (_, i) => frameStart(selectedFrameIndex(i, w.start, step, fps), tl));
    }
    const r = effectiveRange(state, duration);
    const out = Array.from({ length: n }, (_, i) => r.start + i * step);
    // BY_COUNT: the last frame sits on the (sampled) end exactly -- the user's `end`, or the pulled-in end over a whole clip.
    if (state.spacing === 'BY_COUNT' && n === requestedFrames(state, duration, fps)) out[n - 1] = sampledEnd(state, duration, fps);
    return out;
}

/**
 * Derived interval for the summary chip: the real one under `BY_INTERVAL`, `(end−start)/(count−1)` under
 * `BY_COUNT` (the REQUESTED count, as ironman derives it) -- over the SAMPLED end when the range reaches
 * the clip's end (round 9, #5: `media/estimate.ts#countSampleEnd`, so "Exactly 12" is 12). R14: from the
 * WRITTEN start and end, as ironman derives it from what it was sent.
 */
export function derivedInterval(state: PickerState, duration: number, fps?: number | null): number {
    if (state.spacing === 'BY_INTERVAL') return state.interval;
    const n = requestedFrames(state, duration, fps);
    if (n <= 1) return 0;
    const w = writtenRange(state, duration, fps);
    return (w.end - w.start) / (n - 1);
}

/**
 * R14Q-26 (QA pass 4): the spacing the SUMMARY CHIP shows. `derivedInterval` is ironman's number over the WRITTEN start -- a quarter frame
 * early, so "5 frames over 2–4 s" read "every 0.502 s" -- while the frames themselves start on the grid. The chip measures from the
 * range's own first FRAME START (the frame containing `start`) to the sampled end: 2–4 s × 5 = "every 0.5 s". Without a rate the start
 * is written as it is and the two agree.
 */
export function displayInterval(state: PickerState, duration: number, fps?: number | null): number {
    if (state.spacing === 'BY_INTERVAL') return state.interval;
    const n = requestedFrames(state, duration, fps);
    if (n <= 1) return 0;
    const r = effectiveRange(state, duration);
    const start = typeof fps === 'number' && fps > 0 && duration > 0 ? frameStart(frameIndex(r.start, createTimeline(duration, fps)), createTimeline(duration, fps)) : r.start;
    const end = state.range ? roundMs(sampledEnd(state, duration, fps)) : sampledEnd(state, duration, fps);
    return Math.max(0, end - start) / (n - 1);
}

/**
 * Round 9 (L3, R8 #5): the end the picker SAMPLES (and asks the server for) -- the range's own end, or,
 * for `BY_COUNT` over a range that reaches the clip's end, the end that makes every requested frame a written one
 * (`countSampleEnd`, over the WRITTEN start: the last frame's own start once `fps` is known (R14, BUG-002), the
 * round-9 pull without it). Read by `derivedInterval`, `frameTimes`, `writtenRange` and `toOptionWrites`.
 * Unrounded; the wire rounds it (`encodeSeconds`).
 */
export function sampledEnd(state: PickerState, duration: number, fps?: number | null): number {
    const r = effectiveRange(state, duration);
    if (state.mode === 'ONE' || state.spacing !== 'BY_COUNT') return r.end;
    const start = state.range ? rangeStartWrite(r.start, duration, fps) : 0;
    return countSampleEnd(Math.max(FRAME_LIMITS.minCount, Math.floor(state.count)), start, r.end, duration, fps);
}

/** `round(duration × fps)` — the every-frame total the amber line states (§6.3, §0b). */
export function everyFrameTotal(duration: number, fps: number, totalFrames?: number | null): number {
    return gridFrames(duration, fps, totalFrames);
}

/** The clip's frame total for the grid: the probe's count when known (R14Q-49 / R14Q-54), else `round(duration × fps)`. */
export function gridFrames(duration: number, fps: number, totalFrames?: number | null): number {
    if (typeof totalFrames === 'number' && Number.isFinite(totalFrames) && totalFrames > 0) return Math.round(totalFrames);
    return Math.max(0, Math.round(duration * fps));
}

/** IT2 (copy N1): the frames the EFFECTIVE range holds at `fps` (`BY_COUNT` only; null when the clip's fps is unknown or the range is empty). */
export function everyFramesInRange(state: PickerState, duration: number, fps?: number | null, totalFrames?: number | null): number | null {
    if (state.mode === 'ONE' || state.spacing !== 'BY_COUNT' || !(typeof fps === 'number' && fps > 0) || !(duration > 0)) return null;
    const r = effectiveRange(state, duration);
    const every = frameStartsWithin(r.start, r.end, duration, fps, totalFrames);
    return every > 0 ? every : null;
}

/**
 * R14Q-43 (QA pass 6): the frames a range HOLDS are the frame STARTS inside [start, end] -- as `deliveredFrames` and the band count them --
 * not the periods its length covers (`round(0.033 × 30)` = 1 for 2.000–2.033, which holds two starts: 2.000 and 2.033). A millisecond of
 * tolerance either side: the range's ends are millisecond-rounded frame starts. Capped at the clip's last frame.
 */
export function frameStartsWithin(start: number, end: number, duration: number, fps: number, totalFrames?: number | null): number {
    if (!(fps > 0) || !(end >= start)) return 0;
    const tol = 1e-3;
    // R14Q-49 / R14Q-54 (QA pass 7): the clip's frame total is the PROBE's (`timeline.frameCount`, the counter's "of 240") when it is known --
    // `round(duration × fps)` re-derived from a container duration read 241 on B and 1190 on the .mpg beside counters saying 240 and 1188.
    const lastFrame = Math.max(0, gridFrames(duration, fps, totalFrames) - 1);
    const first = Math.max(0, Math.ceil((start - tol) * fps));
    const last = Math.min(lastFrame, Math.floor((end + tol) * fps));
    return Math.max(0, last - first + 1);
}

/** §3.2 sheet geometry: rows = ceil(frames / columns); a ragged last row is allowed. */
export function sheetCells(frames: number, columns: number): { cols: number; rows: number; cells: number; remainder: number } {
    const cols = Math.max(1, Math.floor(columns));
    const rows = Math.ceil(frames / cols);
    return { cols, rows, cells: frames, remainder: frames % cols };
}

/** `SHEET_TOO_TALL` (review 7 K5) is the root's: it needs the clip's shape and the output format (`sheet-layout.ts#sheetTooTall`). */
export type ClientRefusal = 'RANGE_INVALID' | 'COUNT_TOO_SMALL' | 'TOO_MANY_FRAMES' | 'SHEET_TOO_LARGE' | 'MORE_THAN_CLIP' | 'SHEET_TOO_TALL';

/**
 * The §3.2 refusals the browser can see before asking the edge. null = nothing to say. IT2 (copy N1): with the clip's `fps`
 * known, a `BY_COUNT` ask above the frames the range holds is `MORE_THAN_CLIP` (ffmpeg would write repeats; the server does
 * not refuse it, so the browser must) -- ahead of the 600 bound, which is the less useful thing to say about it.
 * R14 (BUG-013, parent ruling): a "Several frames" request whose gap is longer than the range -- "every 5 s" over 1.48 s --
 * is NOT a refusal. The server never refused it (its `req.frames` is 1, the frame at `start`) and, since ironman
 * `1ac91e8d`'s select rule (R14 BUG-002), ffmpeg WRITES that one frame (the old `fps` filter wrote none and the ZIP came
 * back empty -- the bug). The client counts it as the one frame it is (`frameCount` = 1, the Result row "1 JPG · one ZIP");
 * the interval picker keeps the longer chips disabled with the reason (`intervalTooLong`) so the user is steered, not stopped.
 */
export function clientRefusal(state: PickerState, duration: number, fps?: number | null): ClientRefusal | null {
    if (state.mode === 'ONE') return null;
    if (!rangeIsValid(state, duration)) return 'RANGE_INVALID';
    if (state.spacing === 'BY_COUNT' && state.count < FRAME_LIMITS.minCount) return 'COUNT_TOO_SMALL';
    // ironman refuses on `req.frames` — the requested number — before it knows what ffmpeg writes.
    const n = requestedFrames(state, duration, fps);
    const every = everyFramesInRange(state, duration, fps);
    if (every !== null && n > every) return 'MORE_THAN_CLIP';
    if (state.mode === 'SHEET') {
        if (n > FRAME_LIMITS.maxSheetCells || state.sheet.width > FRAME_LIMITS.maxSheetWidthPx) return 'SHEET_TOO_LARGE';
    }
    if (n > FRAME_LIMITS.maxFramesPerJob) return 'TOO_MANY_FRAMES';
    return null;
}

/** §4.3: amber past 300 frames. */
export function isAmber(frames: number): boolean {
    return frames > FRAME_LIMITS.amberAt;
}

/** §6.5: does the big-job check show before Convert? Never for a single frame. */
export function needsCheck(state: PickerState, duration: number, heightPx: number, fps?: number | null): boolean {
    if (state.mode === 'ONE') return false;
    const n = requestedFrames(state, duration, fps);
    return n >= CHECK_THRESHOLDS.frames || (duration > CHECK_THRESHOLDS.durationS && heightPx > CHECK_THRESHOLDS.heightPx);
}

// ---------------------------------------------------------------------------------------------
// Actions and the reducer.
// ---------------------------------------------------------------------------------------------

export type PickerAction =
    | { type: 'file'; fileId: string | null }
    | { type: 'seek'; t: number }
    | { type: 'mode'; mode: PickerMode }
    | { type: 'range'; range: TimeRange | null }
    | { type: 'rangeStart'; start: number }
    | { type: 'rangeEnd'; end: number }
    | { type: 'spacing'; spacing: Spacing }
    | { type: 'interval'; interval: number }
    | { type: 'count'; count: number }
    | { type: 'sheet'; sheet: Partial<SheetSettings> }
    | { type: 'step'; step: PickerStep }
    | { type: 'zoom'; zoom: 1 | 2 }
    | { type: 'sharpnessReady'; ready: boolean }
    | { type: 'source'; source: PickerSource }
    /** Round 12 (N3, P4): pin the frame at `t` (the first pin selects Picked frames); unpin one; clear all; choose / leave Picked frames. */
    | { type: 'pin'; t: number }
    | { type: 'unpin'; t: number }
    | { type: 'clearPins' }
    | { type: 'picked'; picked: boolean }
    /** Re-derive from the options context's summary (§6.5 "Reset to defaults" replay). */
    | { type: 'summary'; summary: Record<string, unknown>; /** Round 9 (#5): the clip's length, so the reducer can tell its own sampled-end write from a change (`fromSummary`). */ duration?: number; /** R14: the clip's frame rate (a REAL one, never provisional), so the reducer recognises its own frame-anchored start write. */ fps?: number | null };

function clampInt(n: number, min: number, max: number): number {
    if (!Number.isFinite(n)) return min;
    return Math.min(max, Math.max(min, Math.round(n)));
}

/**
 * Pure. Returns the SAME object when nothing changed, so a `ReplaySubject` subscriber can
 * short-circuit on identity.
 */
export function reducePicker(state: PickerState, action: PickerAction): PickerState {
    switch (action.type) {
        case 'file':
            // Round 12 (P4): the pins were about the clip that goes; a switch back to the same clip keeps them.
            return state.fileId === action.fileId ? state : { ...state, fileId: action.fileId, pins: [], picked: false };
        case 'seek': {
            // R14 (BUG-001): the playhead is carried EXACT -- `1.43333…` is frame 43 at 30 fps; rounded to `1.433` it was
            // inside frame 42 for the element, the write and the export while the counter said 44. Display rounding is
            // `formatHms`'s. An identity seek (float noise under a nanosecond) is not a change.
            const t = Number.isFinite(action.t) ? Math.max(0, action.t) : state.t;
            return Math.abs(t - state.t) < 1e-9 ? state : { ...state, t };
        }
        case 'mode':
            // Round 12 (P4): One frame / Several frames / Contact sheet is a choice AWAY from Picked frames; the pins stay.
            if (state.mode === action.mode) return state.picked ? { ...state, picked: false } : state;
            return { ...state, mode: action.mode, picked: false };
        case 'pin': {
            // R14 (BUG-001): a pin is the exact frame time the playhead holds (see `seek`).
            const t = Number.isFinite(action.t) ? Math.max(0, action.t) : NaN;
            if (!Number.isFinite(t) || state.pins.some((p) => Math.abs(p - t) < PIN_EPSILON) || state.pins.length >= MAX_PINS) return state;
            const pins = [...state.pins, t].sort((a, b) => a - b);
            // Every pin selects Picked frames -- the Output showing the pinned frames IS the feedback that the pin
            // landed -- and the tree goes to ONE (one time per job). One frame / Several / Sheet lead back out.
            return { ...state, pins, picked: true, mode: 'ONE' };
        }
        case 'unpin': {
            const pins = state.pins.filter((p) => Math.abs(p - action.t) >= PIN_EPSILON);
            if (pins.length === state.pins.length) return state;
            return { ...state, pins, picked: pins.length === 0 ? false : state.picked };
        }
        case 'clearPins':
            return state.pins.length === 0 && !state.picked ? state : { ...state, pins: [], picked: false };
        case 'picked': {
            const picked = action.picked && state.pins.length > 0;
            if (picked === state.picked && (!picked || state.mode === 'ONE')) return state;
            return picked ? { ...state, picked: true, mode: 'ONE' } : { ...state, picked: false };
        }
        case 'range':
            return { ...state, range: action.range ? { ...action.range } : null };
        case 'rangeStart': {
            const end = state.range ? state.range.end : Number.POSITIVE_INFINITY;
            return { ...state, range: { start: Math.max(0, action.start), end } };
        }
        case 'rangeEnd': {
            const start = state.range ? state.range.start : 0;
            return { ...state, range: { start, end: Math.max(0, action.end) } };
        }
        case 'spacing':
            return state.spacing === action.spacing ? state : { ...state, spacing: action.spacing };
        case 'interval': {
            const interval = snapInterval(action.interval);
            return interval === state.interval ? state : { ...state, interval };
        }
        case 'count': {
            const count = clampInt(action.count, FRAME_LIMITS.minCount, FRAME_LIMITS.maxFramesPerJob);
            return count === state.count ? state : { ...state, count };
        }
        case 'sheet': {
            const next: SheetSettings = { ...state.sheet, ...action.sheet };
            next.columns = clampInt(next.columns, 1, FRAME_LIMITS.maxSheetColumns);
            next.width = clampInt(next.width, FRAME_LIMITS.minSheetWidthPx, FRAME_LIMITS.maxSheetWidthPx);
            return { ...state, sheet: next };
        }
        case 'step':
            return state.step === action.step ? state : { ...state, step: action.step };
        case 'zoom':
            return state.zoom === action.zoom ? state : { ...state, zoom: action.zoom };
        case 'sharpnessReady':
            return state.sharpnessReady === action.ready ? state : { ...state, sharpnessReady: action.ready };
        case 'source':
            return state.source === action.source ? state : { ...state, source: action.source };
        case 'summary':
            return fromSummary(state, action.summary, action.duration, action.fps);
        default:
            return state;
    }
}

/**
 * Re-derive the option-backed fields from the context's summary (§6.5: the picker subscribes to
 * `modelChangeObserver` and re-derives so the two panels never disagree). Fields the summary
 * does not carry (`t` under a non-ONE mode, `zoom`, `source`…) are kept. Unknown or malformed
 * values leave the field as it was — a half-written summary must not zero the picker.
 */
export function fromSummary(state: PickerState, summary: Record<string, unknown>, duration?: number, fps?: number | null): PickerState {
    if (!summary || typeof summary !== 'object') return state;
    let next: PickerState = state;
    const set = <K extends keyof PickerState>(key: K, value: PickerState[K]) => {
        if (next === state) next = { ...state };
        next[key] = value;
    };

    const mode = modeFromOption(summary.videoToImageFrameSelection);
    if (mode && mode !== state.mode) { set('mode', mode); if (state.picked) set('picked', false); }

    // The playhead belongs to the picker while editing: the tree's `screenshotTime` is written on
    // Convert (design §6.5), so between seeks it is stale — usually 0 — and adopting it on every
    // emission (activation, any option change, the idle-deferred emit) snapped the playhead back
    // to 0:00 after a scrub (QA 2026-09-18, Q6). Adopt it only while the picker's own playhead is
    // still at 0: a time typed in Quick convert's field, or a restored record, seeds the picker once.
    // R14: adopted EXACT; the root re-snaps a seeded / restored playhead to the nearest frame start once the clip's rate is known.
    const t = decodeUnitValue(summary.screenshotTime);
    if (t !== null && t >= 0 && (mode ?? state.mode) === 'ONE' && state.t === 0 && t !== state.t) set('t', t);

    if (summary.screenshotSpacing === 'BY_INTERVAL' || summary.screenshotSpacing === 'BY_COUNT') {
        if (summary.screenshotSpacing !== state.spacing) set('spacing', summary.screenshotSpacing);
    }

    const interval = decodeInterval(summary.screenshotFramerate);
    if (interval !== null && interval !== state.interval) set('interval', interval);

    const count = decodeUnitValue(summary.screenshotFrameCount);
    if (count !== null && Number.isInteger(count) && count >= FRAME_LIMITS.minCount && count !== state.count) set('count', count);

    // R14 (QA pass 4, R14Q-24 P2): since R14Q-13 an UNSET bound posts '' (3663714fc), so the two KEYS are always in the summary and
    // decode to nothing -- every commit through the tree (a typed count, an interval chip, a mode switch, the aligners' quiet writes)
    // used to read that as "whole clip" and throw the user's range away; a row re-click (no commit) kept it. A bound that decodes to
    // nothing is UNCHANGED, never a clear: an explicit clear comes from the range controls (`{ type: 'range', range: null }`, the
    // strip's Reset key), not from an echo.
    const start = decodeUnitValue(summary.screenshotStartTime);
    const end = decodeUnitValue(summary.screenshotEndTime);
    if (start !== null || end !== null) {
        const range: TimeRange = { start: start ?? 0, end: end ?? Number.POSITIVE_INFINITY };
        // Round 9 (#5): the tree echoing the picker's OWN write -- the sampled end of a BY_COUNT range over
        // the clip's end (`toOptionWrites`) -- is not a change of range; adopting it would turn "whole clip"
        // into a range ending a step short, and the Reset key would light for nothing the user did.
        // R14 (spec 5): the start is written a quarter frame early (`rangeStartWrite`) -- `ownRangeWrite` returns that very
        // number, so the echo matches at the wire's precision; adopting it would move the range a frame back on every echo.
        const own = duration !== undefined ? ownRangeWrite(state, duration, fps) : null;
        const echo = own !== null && sameSeconds(own.start, start) && sameSeconds(own.end, end);
        const same = echo
            || (state.range === null && range === null)
            || (state.range !== null && range !== null && state.range.start === range.start && state.range.end === range.end);
        if (!same) set('range', range);
    }

    const sheet: SheetSettings = { ...state.sheet };
    let sheetChanged = false;
    const cols = decodeUnitValue(summary.sheetColumns);
    if (cols !== null && cols !== sheet.columns) { sheet.columns = clampInt(cols, 1, FRAME_LIMITS.maxSheetColumns); sheetChanged = true; }
    const width = decodeUnitValue(summary.sheetWidth);
    if (width !== null && width !== sheet.width) { sheet.width = clampInt(width, FRAME_LIMITS.minSheetWidthPx, FRAME_LIMITS.maxSheetWidthPx); sheetChanged = true; }
    if (typeof summary.sheetTimestamps === 'boolean' && summary.sheetTimestamps !== sheet.timestamps) { sheet.timestamps = summary.sheetTimestamps; sheetChanged = true; }
    if (typeof summary.sheetHeader === 'boolean' && summary.sheetHeader !== sheet.header) { sheet.header = summary.sheetHeader; sheetChanged = true; }
    if (sheetChanged) set('sheet', sheet);

    return next;
}

/**
 * The range keys the picker WRITES for this state (round 9, #5): the user's range as it is, except a
 * `BY_COUNT` range that reaches the clip's end, whose end is the sampled one (`sampledEnd`) so the server
 * writes every requested frame. `null` = the key is written empty (= clip start / clip end). Shared by
 * `toOptionWrites` and `fromSummary`, so the reducer recognises the tree echoing this very write.
 */
export function ownRangeWrite(state: PickerState, duration: number, fps?: number | null): { start: number | null; end: number | null } {
    if (state.mode === 'ONE') return { start: null, end: null };
    const r = effectiveRange(state, duration);
    const end = sampledEnd(state, duration, fps);
    const pulled = Number.isFinite(duration) && duration > 0 && end < r.end;
    // R14 (BUG-023/024): a user's start is written frame-anchored, a quarter frame early (`rangeStartWrite`).
    if (state.range) return { start: rangeStartWrite(r.start, duration, fps), end: pulled ? end : r.end };
    return pulled ? { start: null, end } : { start: null, end: null };
}

/** Two written seconds are the same when they round to the same millisecond (`encodeSeconds` carries 3 decimals). */
function sameSeconds(a: number | null, b: number | null): boolean {
    if (a === null || b === null) return a === b;
    return Math.abs(a - b) < 0.0015;
}

/**
 * The partial summary that puts the picker's choices into the options context (§6.5), keyed by
 * the xconfig property names (§3.1). Only the keys the MODE uses are written; the panel already
 * owns everything else, and a key the mode does not use must stay whatever the tree holds.
 * `null` range → both time keys empty (= clip start / clip end). `writeTime` (IT1, qa #2) maps the ONE-frame
 * playhead to the instant written -- the root passes `frameSeekTime` with the presented pts; absent, the playhead as is.
 * `fps` (R14, BUG-023/024): the clip's real frame rate anchors the range start a quarter frame early (`rangeStartWrite`).
 */
export function toOptionWrites(state: PickerState, duration: number, writeTime?: (t: number) => number, fps?: number | null): Record<string, string | boolean> {
    const writes: Record<string, string | boolean> = {
        videoToImageFrameSelection: MODE_TO_OPTION[state.mode],
    };
    if (state.mode === 'ONE') {
        // IT1 (W3, qa #2): the instant ffmpeg is asked for is the FRAME's (`media/timeline.ts#frameSeekTime`), not the
        // rounded playhead -- `-ss` delivers the first frame with pts ≥ t, and 7.26667 rounded to 7.267 was the next one.
        writes.screenshotTime = encodeSeconds(writeTime ? writeTime(state.t) : state.t);
        return writes;
    }
    writes.screenshotSpacing = state.spacing;
    if (state.spacing === 'BY_INTERVAL') writes.screenshotFramerate = framerateOptionFor(state.interval);
    else writes.screenshotFrameCount = String(Math.max(FRAME_LIMITS.minCount, Math.floor(state.count)));
    const own = ownRangeWrite(state, duration, fps);
    writes.screenshotStartTime = own.start === null ? '' : encodeSeconds(own.start);
    writes.screenshotEndTime = own.end === null ? '' : encodeSeconds(own.end);
    if (state.mode === 'SHEET') {
        writes.sheetColumns = String(state.sheet.columns);
        writes.sheetWidth = encodePixels(state.sheet.width);
        writes.sheetTimestamps = state.sheet.timestamps;
        writes.sheetHeader = state.sheet.header;
    }
    return writes;
}
